import React, { useContext } from "react"
import Title from "@love-irish/core-title"
import { IconBook, IconBook2, IconSearch } from "@tabler/icons-react"
import classNames from "classnames"
import { MediaPreview } from "@love-irish/resource-media-previews"
import { MediaPreviewContext } from "../media_preview_context"
import Tooltip from "design_system/core/components/tooltip"
import Content from "@love-irish/core-content"
import formatter from "design_system/utils/formatter"
import { CodexFieldLabel } from "@love-irish/resource-codex-meta-components"
import { ResourceActions } from "../shared/resource_actions"
import CustomFieldBadge from "@love-irish/resource-custom-field-badge"
import ProfileCurrentLevel from "@love-irish/community-profile-current-level"
import { actionKeys, actionTranslations, labels } from "@love-irish/i18n"
import {
  ReadMoreLink,
  ResourceMetaFooter,
  TopicList,
  TypeLabel,
} from "../shared"
import {
  CodexMeta,
  CodexTagList,
} from "@love-irish/resource-codex-meta-components"
import PronunciationLabelGroup from "@love-irish/resource-pronunciation-label"
import { format as toOghamScript } from "@love-irish/adventure-ogham-script"
import { LanguageFlags } from "@love-irish/cms-language-selector"
import { twMerge } from "tailwind-merge"

// We support a simple version of liquid type tags in codex record names to help
// with examples. Right now we use @grug as the data source for the dynamic tag.
export function addDynamicTags(string = "") {
  return string.replace("_name_", "_@grug_")
}

const BaseListItem = ({
  className = "",
  content = null,
  addon = true,
  addToCollection = true,
  footer,
  displayAudioInfo = false,
  level = 0,
  display,
  item,
  collections,
  currentUser,

  media,
  // Type-specific rich content (embeds, meta rows, audio, EXP, …) appended to the end of the content
  // area, below the main body and above the footer. Lets a type extend the shell without a new prop.
  children,

  // Inline meta slot rendered on the same row as the language flags (e.g. a song's recordings count and
  // Lyrics link). Small, glanceable per-type info that reads alongside the flags rather than below.
  meta,

  // Full-width slot at the very top of the card, above the type label (e.g. a mini-game preview banner).
  banner,
  // Extra buttons in the header actions area, rendered before ResourceActions (e.g. play / audio).
  headerActions,

  showActions = true,

  typeIcon,
  typeLabel,
  typeTooltip,

  // Per-type "read more" CTA, e.g. { useI18n: true, actionKey: actionKeys.LISTEN } for a bilingual "Éist · Listen".
  // Falls back to display.readMore, then the default "Léigh mó · Read more". Pass `false` to hide it.
  readMore: readMoreProp,
  // data-testid on the card root; each type can scope it (default is generic so tests target by type label).
  testId = "resource-list-item",

  showOgham = false,
  ...props
}) => {
  // Rich media is the default for a section's items: render the resource's MediaPreview (record & sleeve,
  // book, cover) in place of the plain thumbnail — unless the resolved variant is "plain", the opt-out that
  // keeps this per-type item's own thumbnail (with its overlays). No preview context = the plain thumbnail.
  const previewOptions = useContext(MediaPreviewContext)
  const effectiveMedia =
    previewOptions && previewOptions.variant !== "plain" ? (
      // shrink-0 slot so the preview keeps its intrinsic width in the card's flex row — a book cover's
      // outer anchor is only w-fit (no shrink-0 of its own), so without this it collapses to a sliver.
      <div className="shrink-0">
        <MediaPreview resource={item} {...previewOptions} />
      </div>
    ) : (
      media
    )

  // Chrome options (default to the card look). A divided list passes { bordered: false } etc.
  const bordered = display?.bordered ?? true
  const showTypeLabel = display?.showTypeLabel ?? true
  const readMore = readMoreProp ?? display?.readMore ?? true
  // The record name transliterated into the Ogham alphabet (empty when the name has no letters).
  const oghamName = item?.name ? toOghamScript(item.name) : ""

  const headerDefinition = item.__typename === "ItemCodexRecord"

  return (
    <div
      className={classNames(
        " @container/list-item bg-background min-w-[100px] max-w-full",
        bordered && "border rounded-xl dark:border-dark-700",
        className
      )}
      data-testid={testId}
      // Tags every resource card (all 30+ types compose through here) for web-vitals attribution; the
      // variant is the active media-preview style when one is in effect.
      data-vitals-component="resource-list-item"
      data-vitals-variant={previewOptions?.variant}
    >
      {banner}
      {showTypeLabel && (
        <div className="px-4 pt-4 text-xs flex gap-1 justify-between items-center">
          <div className="flex items-center gap-3">
            <TypeLabel
              icon={typeIcon}
              label={typeLabel}
              tooltip={typeTooltip}
            />
            {item.levelProgress?.currentLevel && (
              <ProfileCurrentLevel progress={item.levelProgress} />
            )}
          </div>

          <div className="flex shrink-0 gap-1">
            {headerActions}
            {showActions && <ResourceActions item={item} />}
          </div>
        </div>
      )}
      <div className={twMerge("overflow-hidden", bordered ? "p-4" : "py-4")}>
        <div className="flex justify-between items-start gap-8 ">
          <Title
            className={twMerge(
              "flex gap-2 text-2xl",
              headerDefinition && "text-definition"
            )}
            title={addDynamicTags(item.name)}
            size="sm"
            showIcon={false}
            href={item.path}
            {...props}
          />
        </div>

        <div className="space-y-4 mt-2">
          <PronunciationLabelGroup resource={item} />

          {item.translation && (
            <div className="mt-2">
              <CodexFieldLabel>
                {labels.translation.translation} / {labels.translation.text}
              </CodexFieldLabel>
              <Content
                content={addDynamicTags(item.translation)}
                className="prose-sm text-inherit text-sm text-dark dark:text-white font-mono! first-line:not-italic"
              />
            </div>
          )}
        </div>

        <div className="flex gap-8 justify-between mt-3 items-start ">
          <div className="self-stretch flex justify-between flex-col">
            <div>
              {item.sections && item.sections.length > 0 ? (
                // Enriched with senses: render the POS-grouped section format (condensed).
                <div className="mt-2">
                  <CodexMeta item={item} showPronunciation={false} />
                </div>
              ) : (
                <>
                  {(item.summary || item.content) && (
                    <div className="mt-2">
                      <Content
                        content={item.summary || item.content}
                        className="prose-sm text-inherit text-sm text-dark dark:text-white font-mono! first-line:not-italic"
                      />
                    </div>
                  )}
                </>
              )}

              {((item.languages && item.languages.length > 0) || meta) && (
                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2">
                  {item.languages && item.languages.length > 0 && (
                    <LanguageFlags codes={item.languages} size={16} />
                  )}
                  {meta}
                </div>
              )}

              <TopicList itemTopics={item.itemTopics} />

              {showOgham && oghamName && (
                <div className="mt-3">
                  <Tooltip
                    title={`'${item.name}' in Ogham, an ancient Celtic writing system`}
                  >
                    {/* aria-label (not role="img") gives screen readers the name without colliding
                      with the item image's role; the glyphs alone would read as gibberish. */}
                    <span
                      data-testid="codex-ogham"
                      aria-label={`'${item.name}' in Ogham, an ancient Celtic writing system`}
                      className="ogham-glyph text-xl leading-none text-secondary-500"
                    >
                      {oghamName}
                    </span>
                  </Tooltip>
                </div>
              )}

              {item.customFields && item.customFields.length > 0 && (
                <div className="p-2">
                  <hr />
                  <div className="mt-2 flex flex-wrap gap-2">
                    {item.customFields.map((field) => (
                      <CustomFieldBadge key={field.key} field={field} />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {readMore !== false && (
              <ReadMoreLink
                href={item.path}
                {...(typeof readMore === "object" ? readMore : {})}
              />
            )}
          </div>

          {effectiveMedia}
        </div>

        {children}
      </div>

      <ResourceMetaFooter
        profile={item.profile}
        createdAt={item.createdAt}
        publishedAt={item.publishedAt}
        updatedAt={item.updatedAt}
        customContent={footer}
      />
    </div>
  )
}

export default BaseListItem
